import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Box,
  Typography,
  SwipeableDrawer,
  useMediaQuery,
  useTheme,
  SxProps,
  Theme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

interface AppDialogProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  actions?: React.ReactNode
  titleAction?: React.ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg'
  disableEnforceFocus?: boolean
  /** Forza il Dialog centrato anche su mobile, invece del bottom-sheet di default. */
  centeredOnMobile?: boolean
  /**
   * Passato al Drawer/Dialog radice. Serve a chi apre questo dialog da dentro un
   * altro overlay con z-index alzato (es. il Drawer di components-new/data/Filters.tsx),
   * per non finire visivamente sotto di esso — stesso bisogno di FILTERS_POPUP_Z_INDEX lì.
   */
  sx?: SxProps<Theme>
}

export default function AppDialog({
  open,
  onClose,
  title,
  children,
  actions,
  titleAction,
  maxWidth = 'sm',
  disableEnforceFocus = false,
  centeredOnMobile = false,
  sx,
}: AppDialogProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const useBottomSheet = isMobile && !centeredOnMobile

  // Il click fuori dal dialog non lo chiude: si chiude solo con la X (o Esc),
  // per non perdere quanto compilato con un click accidentale.
  const handleRootClose = (_event?: unknown, reason?: string) => {
    if (reason === 'backdropClick') return
    onClose()
  }

  const header = (
    <>
      {useBottomSheet && (
        <Box sx={{display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5, flexShrink: 0}}>
          <Box sx={{width: 40, height: 4, borderRadius: 2, backgroundColor: NEUTRAL_SCALE[300]}} />
        </Box>
      )}
      <DialogTitle sx={{pb: 1.5, flexShrink: 0}}>
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <Typography sx={{fontSize: 18, fontWeight: 700, color: 'text.primary', lineHeight: 1.3, flex: 1, minWidth: 0}}>
            {title}
          </Typography>
          {titleAction}
          <IconButton aria-label='close' onClick={onClose} size='small' sx={{ml: 1, flexShrink: 0}}>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider sx={{flexShrink: 0}} />
    </>
  )

  if (useBottomSheet) {
    return (
      <SwipeableDrawer
        anchor='bottom'
        open={open}
        onClose={handleRootClose}
        onOpen={() => {}}
        disableSwipeToOpen
        ModalProps={{disableEnforceFocus}}
        sx={sx}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            // `dvh` (not `vh`): on a real phone, `vh` is the LARGEST possible viewport —
            // as if the browser's address/tab bar were already hidden — so `90vh` computes
            // taller than what's actually visible while that chrome is showing. This sheet
            // is `position: fixed; bottom: 0` (SwipeableDrawer's anchor='bottom'), and with
            // the page's scroll locked behind an open modal, any part of it beyond the truly
            // visible area — typically the actions row, since it's what sits lowest — is
            // simply unreachable, not just clipped. `dvh` tracks the real, current viewport.
            maxHeight: '90dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            pb: 'env(safe-area-inset-bottom)',
          },
        }}
      >
        {header}
        {/* minHeight: 0 overrides the flex item's default min-height:auto, which
            otherwise keeps this content area at its content's full (min-content)
            height instead of shrinking to the space left by header/actions. Without
            it, tall form content pushes the actions row past the Paper's maxHeight,
            and since the Paper has overflow:'hidden' (not scrollable itself), the
            actions end up silently clipped off — invisible and unreachable. */}
        <DialogContent sx={{flex: 1, overflowY: 'auto', minHeight: 0}}>{children}</DialogContent>
        {actions && (
          <DialogActions sx={{px: 3, pt: 0.5, pb: 2, flexShrink: 0}}>{actions}</DialogActions>
        )}
      </SwipeableDrawer>
    )
  }

  return (
    <Dialog open={open} onClose={handleRootClose} maxWidth={maxWidth} fullWidth disableEnforceFocus={disableEnforceFocus} sx={sx}>
      {header}
      <DialogContent>{children}</DialogContent>
      {actions && <DialogActions sx={{px: 3, pt: 0.5, pb: 2}}>{actions}</DialogActions>}
    </Dialog>
  )
}
